import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { X, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { supabasePostsService } from "@/services/supabasePostsService";
import { resolveDropdownNavigation, isMentionNotification } from "@/services/notificationDropdownNav";
import { getNotificationText } from "@/services/notificationText";
import { useAuth } from "@/contexts/AuthContext";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { hebrewTimeAgo } from "@/lib/timeAgo";

interface NotificationsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  triggerRef: React.RefObject<HTMLButtonElement>;
  onCountChange?: (count: number) => void;
}

const NotificationsDialog = ({ open, onOpenChange, triggerRef, onCountChange }: NotificationsDialogProps) => {
  const { user } = useAuth();
  const { effectiveUserId } = useImpersonation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("inbox");
  const [notificationsList, setNotificationsList] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  // A failed load isn't "אין התראות" — keep the two apart.
  const [loadFailed, setLoadFailed] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (open && effectiveUserId) {
      loadNotifications();
      loadUnreadCount();
    }
  }, [open, effectiveUserId, activeTab]);

  useEffect(() => {
    if (!effectiveUserId) return;
    const channel = supabase
      .channel(`notifications-changes-${effectiveUserId}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'user_notifications',
        filter: `user_id=eq.${effectiveUserId}`
      }, () => {
        loadNotifications();
        loadUnreadCount();
      })
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'user_mention_notifications',
        filter: `user_id=eq.${effectiveUserId}`
      }, () => {
        loadNotifications();
        loadUnreadCount();
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [effectiveUserId]);

  const loadNotifications = async () => {
    if (!effectiveUserId) return;
    setLoading(true);
    setLoadFailed(false);
    try {
      const data = await supabasePostsService.getNotifications(
        activeTab === 'all' ? 'all' : activeTab === 'mentions' ? 'mentions' : 'inbox',
        effectiveUserId
      );
      setNotificationsList(data);
    } catch (error) {
      console.error('Error loading notifications:', error);
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  };

  const loadUnreadCount = async () => {
    try {
      const count = await supabasePostsService.getUnreadNotificationsCount(effectiveUserId);
      setUnreadCount(count);
      onCountChange?.(count);
    } catch (error) {
      console.error('Error loading unread count:', error);
    }
  };

  const handleNotificationClick = async (notification: any) => {
    // Mark as read in the correct table
    try {
      if (isMentionNotification(notification)) {
        await supabasePostsService.markMentionNotificationAsRead(notification.id);
      } else {
        await supabasePostsService.markNotificationAsRead(notification.id);
      }
      loadNotifications();
      loadUnreadCount();
    } catch (error) {
      console.error('Error marking as read:', error);
    }

    // Close dialog
    onOpenChange(false);

    const nav = resolveDropdownNavigation(notification);
    if (nav === null) return;
    if (nav === 'toast_deleted') {
      const isEvent = notification.type === 'new_event' || notification.type === 'event_reminder';
      toast.error(isEvent ? 'האירוע כבר לא קיים' : 'התוכן כבר לא קיים');
      if (!isEvent) navigate('/feed');
      return;
    }
    if (nav.state) navigate(nav.path, { state: nav.state });
    else navigate(nav.path);
  };

  const handleMarkAllAsRead = async () => {
    try {
      await supabasePostsService.markAllNotificationsAsRead();
      loadNotifications();
      loadUnreadCount();
    } catch (error) {
      console.error('Error marking all as read:', error);
      toast.error('לא הצלחנו לסמן את ההתראות כנקראו. נסו שוב.');
    }
  };


  const getTimeAgo = (date: string): string => hebrewTimeAgo(date);

  const tabItems = [
    { id: "inbox", label: "דואר נכנס", count: unreadCount },
    { id: "mentions", label: "תיוגים", count: 0 },
    { id: "all", label: "הכל", count: notificationsList.length }
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        position="fixed-to-header" 
        triggerRef={triggerRef} 
        className="w-[min(450px,95vw)] max-h-[80vh] p-0 gap-0 overflow-hidden app-container shadow-lg"
        hideOverlay
      >
        <DialogDescription className="sr-only">רשימת ההתראות שלך</DialogDescription>
        <div className="flex items-center justify-between p-3 border-b">
          <DialogTitle className="text-lg font-bold">התראות</DialogTitle>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <Button variant="ghost" size="sm" onClick={handleMarkAllAsRead}>
                סימון הכל כנקרא
              </Button>
            )}
            <Button variant="ghost" size="icon" className="h-7 w-7 tap-target" onClick={() => onOpenChange(false)} aria-label="סגירה">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
        
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full h-[calc(500px-44px)]">
          <div className="border-b">
            <ScrollArea className="max-w-full">
              <TabsList className="flex w-full rounded-none bg-transparent h-auto">
                {tabItems.map((tab) => (
                  <TabsTrigger
                    key={tab.id}
                    value={tab.id}
                    className="flex-1 px-2 py-1 text-xs font-medium data-[state=active]:text-primary data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none whitespace-nowrap"
                  >
                    {tab.label}
                    {tab.count > 0 && <span className="me-1">({tab.count})</span>}
                  </TabsTrigger>
                ))}
              </TabsList>
              <ScrollBar orientation="horizontal" />
            </ScrollArea>
          </div>
          
          <ScrollArea className="h-[calc(500px-85px)]">
            {tabItems.map((tab) => (
              <TabsContent key={tab.id} value={tab.id} className="p-0">
                {loading ? (
                  <div className="flex flex-col items-center justify-center h-[300px]">
                    <Loader2 className="h-8 w-8 animate-spin text-primary mb-2" />
                    <p className="text-sm text-muted-foreground">טוען התראות...</p>
                  </div>
                ) : loadFailed && notificationsList.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-[300px] p-4 gap-3 text-center">
                    <p className="text-sm text-muted-foreground">לא הצלחנו לטעון את ההתראות. בדקו את החיבור ונסו שוב.</p>
                    <Button variant="outline" size="sm" className="min-h-11" onClick={() => loadNotifications()}>
                      לנסות שוב
                    </Button>
                  </div>
                ) : notificationsList.length > 0 ? (
                  <div className="divide-y">
                    {notificationsList.map((notification) => (
                      <div
                        key={notification.id}
                        className={`p-4 active:bg-muted/50 cursor-pointer transition-colors ${!notification.is_read ? 'bg-primary/5' : ''}`}
                        onClick={() => handleNotificationClick(notification)}
                      >
                        <div className="flex items-start gap-3 text-right">
                          <Avatar className="h-10 w-10">
                            <AvatarImage
                              src={(notification.actor?.avatar_url || notification.mentioned_by?.avatar_url) ?? undefined}
                              alt={notification.actor?.display_name || notification.mentioned_by?.display_name || 'משתמש'}
                            />
                            <AvatarFallback>
                              {(notification.actor?.display_name || notification.mentioned_by?.display_name || 'מ')[0]}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm" data-private>{getNotificationText(notification)}</p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {getTimeAgo(notification.created_at)}
                            </p>
                          </div>
                          {!notification.is_read && (
                            <div className="h-2 w-2 shrink-0 rounded-full bg-primary">
                              <span className="sr-only">לא נקראה</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-[300px] p-4">
                    <h3 className="text-sm font-semibold text-muted-foreground mb-2">אין התראות</h3>
                    <p className="text-xs text-muted-foreground text-center">כשיהיו לך התראות חדשות, הן יופיעו כאן</p>
                  </div>
                )}
              </TabsContent>
            ))}
          </ScrollArea>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default NotificationsDialog;
